<!-- @format -->
<template>
    <div v-cloak class="social-media-inbox-comments-widget">
        <div class="widget-header">
            <h3>@ Comment</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>

        <div id="inboxCommentsWidget" class="content-wrapper">
            <template v-if="isLoading">
                <div class="loading-screen-container">
                    <loading-screen-svg />
                </div>
            </template>
            <template v-else>
                <div class="h-row">
                    <div class="h-col-md-1"></div>
                    <div class="h-col-md-10">
                        <div
                            v-if="options.isClosed"
                            class="social-inbox-card card-conversation-closed text-center"
                        >
                            <span>
                                <strong class="text-capitalize">{{ options.closedBy }}</strong>
                                closed this conversation on
                                <strong>{{
                                    $moment(options.closedDate) | momentDateMMDDYYYY
                                }}</strong>
                                at
                                <strong>{{ $moment(options.closedDate) | momentTime }}</strong>
                            </span>
                        </div>

                        <div class="social-inbox-card social-post">
                            <generic-post-preview
                                :content="postContent"
                                :date-posted="postDateCreated"
                                :images="postMediaFiles"
                                :profile-image="options.profileImage"
                                :show-redirect-btn="false"
                                :social-network-name="options.socialNetwork"
                                :title="options.accountName"
                                :url-extract="postSharedPage"
                            />
                        </div>

                        <social-inbox-comments
                            :account-name="options.accountName"
                            :comments="[parentComment]"
                            :is-closed="options.isClosed"
                            :social-network="options.socialNetwork"
                            @deleted="handleDeleted(true)"
                        />

                        <social-inbox-comments
                            :account-name="options.accountName"
                            :comments="childComments"
                            :is-closed="options.isClosed"
                            :social-network="options.socialNetwork"
                            @deleted="handleDeleted"
                        />

                        <social-inbox-reply-message
                            v-if="!options.isClosed"
                            ref="replyMessageInput"
                            v-model="replyComment"
                            :client-id="options.clientId"
                            :is-file-upload-enabled="false"
                            :max-media-limit="options.socialNetwork === 'twitter' ? 4 : 1"
                            :profile-image="options.profileImage"
                            :social-network="options.socialNetwork"
                        />
                    </div>
                </div>
            </template>
        </div>

        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" :on-click="closeWidget">Close</app-button>
            </div>
            <div class="action-container">
                <app-button
                    :class="{ 'white-btn': options.isClosed }"
                    :disabled="disableBtn"
                    :loading="loadingBtn"
                    :on-click="handleClick"
                >
                    <template v-if="options.isClosed">
                        <t26-builder-reopen />
                        <span>Re-open Conversation</span>
                    </template>
                    <template v-else>Send</template>
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { SocialMediaPostsMixin } from '@/mixins/social-media-posts-mixin';
import { SocialMediaPostConversationsService } from '@/scripts/services/social-media/social-media-post-conversations-service';

const AppButton = () => import('@/components/common/app-button');
const GenericPostPreview = () =>
    import('@/components/social-media/single-post/generic-post-preview');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const SocialInboxComments = () => import('@/components/social-media/inbox/social-inbox-comments');
const SocialInboxReplyMessage = () =>
    import('@/components/social-media/inbox/social-inbox-reply-message');
const T26tBuilderReopen = () => import('@/components/svg/grid/t26-builder-reopen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

export default {
    name: 'social-media-inbox-comments-widget',

    components: {
        'app-button': AppButton,
        'generic-post-preview': GenericPostPreview,
        'loading-screen-svg': LoadingScreenSvg,
        'social-inbox-comments': SocialInboxComments,
        'social-inbox-reply-message': SocialInboxReplyMessage,
        't26-builder-reopen': T26tBuilderReopen,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },

        options: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            childComments: [],
            disableBtn: false,
            isLoading: false,
            loadingBtn: false,
            parentComment: {},
            postContent: '',
            postDateCreated: '',
            postId: '',
            postMedia: [],
            postSharedPage: null,
            replyComment: {
                image: null,
                message: '',
                parentCommentId: 0,
                isCommentClosed: null,
            },
        };
    },

    mixins: [SocialMediaPostsMixin],

    computed: {
        parentCommentId() {
            return this.options.parentCommentId;
        },

        postMediaFiles() {
            return this.postMedia
                ? this.postMedia.map(({ FileUrl, MimeType }) => ({
                      src: FileUrl,
                      type: MimeType,
                  }))
                : null;
        },
    },

    async created() {
        await this.init();
    },

    methods: {
        async init() {
            this.isLoading = true;
            await this.getComments();
            this.isLoading = false;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getComments() {
            SocialMediaPostConversationsService.getComment(
                this.parentCommentId,
                this.options.clientId
            )
                .then(({ data }) => {
                    const postComments = data.data;
                    const {
                        CommentsChild: childrenComments,
                        ...parentComment
                    } = postComments.Comments;

                    this.childComments = childrenComments;
                    this.postContent = postComments.PostText;
                    this.postDateCreated = this.$moment(postComments.PostedAt).fromNow();
                    this.postMedia = postComments.PostMediaUrls;
                    this.postSharedPage = postComments.PageShareUrl;
                    this.parentComment = parentComment;
                    this.postId = postComments.PostId;

                    EventBus.$emit('social-inbox:getConversations');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting post details. Please try again.',
                        { error },
                    );
                    this.closeWidget();
                });
        },

        handleClick() {
            if (this.options.isClosed) {
                this.reopenConversation();
            } else {
                const hasMedia = this.replyComment.image && this.replyComment.image.length > 0;

                let params = {
                    commentParentId: this.parentCommentId,
                    commentText: this.replyComment.message,
                    isReply: true,
                    postId: this.postId,
                };

                if (hasMedia) {
                    if (this.options.socialNetwork === 'twitter') {
                        const commentMedias = [];

                        for (const element of this.replyComment.image) {
                            let dataMedia = {};
                            const { fileId, isLibrary, mediaUrl, fileUrl = null } = element;

                            if (isLibrary) {
                                dataMedia = { url: fileUrl, previewImageUrl: mediaUrl };
                            }

                            dataMedia = { ...dataMedia, fileId };
                            commentMedias.push(dataMedia);
                        }

                        params = { ...params, commentMediaIds: commentMedias };
                    } else {
                        const {
                            fileId,
                            isLibrary,
                            mediaUrl,
                            fileUrl = null,
                        } = this.replyComment.image[0];

                        if (isLibrary) {
                            params = { ...params, url: fileUrl, previewImageUrl: mediaUrl };
                        }

                        params = { ...params, fileId };
                    }
                }

                if (this.replyComment.message || hasMedia) {
                    this.sendComment(params).then(() => {
                        this.$refs.replyMessageInput.clearInput();

                        let timeOut = 1000;

                        if (hasMedia) {
                            timeOut = this.replyComment.image.length > 2 ? 4000 : 2500;
                        }

                        setTimeout(async () => await this.getComments(), timeOut);
                        this.$helpers.showSuccessNotification('The comment was sent succesfully');
                    });
                }
            }
        },

        handleDeleted(isParent = false) {
            if (!isParent && this.options.commentId !== this.parentCommentId) {
                return this.getComments();
            }

            EventBus.$emit('social-inbox:getConversations');
            this.closeWidget();
        },

        reopenConversation() {
            this.disableBtn = true;
            this.loadingBtn = true;

            SocialMediaPostConversationsService.toggleCommentsStatus(this.options.clientId, {
                commentIds: [this.parentCommentId],
                isClosed: false,
            })
                .then(({ data }) => {
                    if (data.message === 'Ok') {
                        this.disableBtn = false;
                        this.$helpers.showSuccessNotification(
                            'The comment was re-opened succesfully'
                        );

                        EventBus.$emit('social-inbox:getConversations');
                        this.closeWidget();
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while trying to re-open the conversation. Please try again.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loadingBtn = false;
                    this.disableBtn = false;
                });
        },

        sendComment(params) {
            this.disableBtn = true;
            this.loadingBtn = true;

            return new Promise((resolve) =>
                SocialMediaPostConversationsService.postComment(
                    this.options.clientId,
                    this.options.socialNetwork,
                    params
                )
                    .then(({ data }) => {
                        if (data.message === 'Ok') {
                            resolve();
                        }
                    })
                    .catch((e) => {
                        const errorMessage =
                            e.response.status === 409
                                ? e.response.data.data
                                : 'An error occurred while trying to post the comment. Please try again.';

                        this.$helpers.showFailNotification(errorMessage, { error: e });
                    })
                    .finally(() => {
                        this.loadingBtn = false;
                        this.disableBtn = false;
                    })
            );
        },
    },
};
</script>

<style>
.social-media-inbox-comments-widget .loading-screen-container {
    height: 200px;
    overflow: hidden;
    text-align: center;
}

.social-media-inbox-comments-widget .loading-screen-container .loading-screen {
    height: 150px;
}

.social-media-inbox-comments-widget .widget-footer .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
    border: 1px solid #877d88;
}

.social-media-inbox-comments-widget .widget-footer .cancel-container .btn.transparent-btn:hover {
    background-color: #e1e0e5;
}

.social-media-inbox-comments-widget .widget-footer .action-container .btn.white-btn {
    background-color: #fff;
    color: #18aeff;
    border-color: #18aeff;
}

.social-media-inbox-comments-widget .widget-footer .action-container .btn.white-btn:hover {
    background-color: #18aeff;
    color: #fff;
}

.social-media-inbox-comments-widget .widget-footer .action-container .btn.white-btn svg {
    width: 27px;
    height: 27px;
}

.social-media-inbox-comments-widget .widget-footer .action-container .btn.white-btn span {
    padding-left: 10px;
}

.social-media-inbox-comments-widget .content-wrapper {
    background: #e9e9ef 0% 0% no-repeat padding-box;
}

.social-media-inbox-comments-widget .content-wrapper::-webkit-scrollbar-thumb {
    background: #877d88 !important;
}

.social-media-inbox-comments-widget .content-wrapper .social-post {
    margin-bottom: 20px;
}

.social-media-inbox-comments-widget .content-wrapper .social-inbox-card.card-conversation-closed {
    font: normal normal normal 14px/17px Roboto;
    color: #382153;
    border-radius: 10px;
    border: 1px solid #e66001;
    padding: 25px 5px;
    margin-bottom: 20px;
}

.social-media-inbox-comments-widget .content-wrapper .social-inbox-card {
    background: #ffffff 0% 0% no-repeat padding-box;
    box-shadow: 0px 3px 6px #8274e819;
    border-radius: 14px;
    padding: 15px;
}

.social-media-inbox-comments-widget .content-wrapper .inbox-current-answer .reply-input {
    font: normal normal 400 16px/25px Roboto;
    border-color: transparent;
    box-shadow: none;
}

.social-media-inbox-comments-widget .text-capitalize {
    text-transform: capitalize !important;
}
</style>
